import { MouseEvent, useMemo } from 'react';
import {
  Box,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';

import { palette } from '../../palette';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { StatusChip } from '../../components/StatusChip';
import OwnerBadge from './OwnerBadge';
import StageProgress from './StageProgress';
import StatusBadge, { getPayoutStatusColor } from './StatusBadge';
import { PayoutWorklistItem } from './types';

interface WorklistTableProps {
  rows: PayoutWorklistItem[];
  loading: boolean;
  selectedSettlementId?: number;
  selectedSettlementSequenceId?: number;
  onSortChange: (field: string) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onRowClick: (settlementId: number, settlementSequenceId?: number) => void;
  showOwner?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '10px',
    height: '100%',
    maxHeight: '100%',
    overflow: 'auto',

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  table: {
    minWidth: '1200px',
    backgroundColor: theme.palette.primary.light,

    '& .MuiTableHead-root .MuiTableCell-root': {
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontStyle: 'normal',
      fontWeight: 500,
      fontFamily: 'Inter',
      fontSize: '14px',
      lineHeight: '16px',
      height: '55px',
      padding: 0,
      paddingLeft: '16px',
      verticalAlign: 'middle',

      '&:first-of-type': {
        borderRadius: '10px 0 0 0',
      },

      '&:last-of-type': {
        borderRadius: '0 10px 0 0',
      },
    },

    '& .MuiTableBody-root .MuiTableCell-root': {
      borderBottom: '1px solid #4A4E5D',
      color: theme.palette.common.white,
      fontStyle: 'normal',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      lineHeight: '16px',
      minHeight: '65px',
      padding: '12px 16px',
      verticalAlign: 'top',
    },

    '& .MuiTableCell-root': {
      whiteSpace: 'nowrap',
    },
  },
  headCell: {
    color: `${theme.palette.text.secondary} !important`,
    fontWeight: '500 !important',
    whiteSpace: 'nowrap',
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  activeRow: {
    backgroundColor: 'rgba(0, 102, 124, 0.14)',
  },
  amount: {
    fontWeight: 600,
  },
  sortableHead: {
    userSelect: 'none',
    '&:hover': {
      color: `${theme.palette.common.white} !important`,
    },
  },
  defaultSortArrow: {
    padding: 0,
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: '1px',
  },
  nonActiveSortArrow: {
    marginBottom: '1px',
    padding: 0,
    svg: {
      color: 'rgba(255, 255, 255, 0.3)',
    },
    '&:hover': {
      svg: {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
  },
  headerLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
}));

interface HeaderConfig {
  label: string;
  sortField: string;
}

const TABLE_HEADERS: HeaderConfig[] = [
  { label: 'Claimant', sortField: 'claimantName' },
  { label: 'Case Reference ID', sortField: 'referenceId' },
  { label: 'Onboarding Status', sortField: 'status' },
  { label: 'Amount', sortField: 'amount' },
  { label: 'Method', sortField: 'paymentMethod' },
  { label: 'Stage', sortField: 'stage' },
  { label: 'Updated', sortField: 'updatedAt' },
];

const WorklistTable = ({
  rows,
  loading,
  selectedSettlementId,
  selectedSettlementSequenceId,
  onSortChange,
  sortBy,
  sortOrder,
  onRowClick,
  showOwner = true,
}: WorklistTableProps) => {
  const { classes, cx } = useStyles();
  const tableHeaders = showOwner
    ? [...TABLE_HEADERS.slice(0, 6), { label: 'Owner', sortField: 'owner' }, ...TABLE_HEADERS.slice(6)]
    : TABLE_HEADERS;

  const settlementWaveLabels = useMemo(() => {
    const labelsBySettlement = new Map<number, Set<string>>();

    rows.forEach((row) => {
      const normalizedLabel = row.label?.trim().toUpperCase();
      if (!normalizedLabel) {
        return;
      }

      const existingLabels = labelsBySettlement.get(row.settlementId) ?? new Set<string>();
      existingLabels.add(normalizedLabel);
      labelsBySettlement.set(row.settlementId, existingLabels);
    });

    return labelsBySettlement;
  }, [rows]);

  const onSortClick = (event: MouseEvent<HTMLTableCellElement>, field: string) => {
    event.stopPropagation();
    onSortChange(field);
  };

  const shouldShowWaveLabel = (row: PayoutWorklistItem) => {
    const normalizedLabel = row.label?.trim().toUpperCase();
    if (!normalizedLabel) {
      return false;
    }

    const labelsForSettlement = settlementWaveLabels.get(row.settlementId);
    if (!labelsForSettlement || labelsForSettlement.size === 0) {
      return false;
    }

    return !(labelsForSettlement.size === 1 && labelsForSettlement.has('W1'));
  };

  return (
    <TableContainer className={classes.container} aria-label="Payout worklist table container">
      <Table className={classes.table} aria-label="Payout worklist table">
        <TableHead>
          <TableRow>
            {tableHeaders.map((head) => (
              <TableCell
                key={head.label}
                className={cx(
                  classes.headCell,
                  classes.sortableHead,
                )}
                onClick={(event) => onSortClick(event, head.sortField)}
                aria-label={
                  `Sort by ${head.label.toLowerCase()} currently ${sortBy === head.sortField ? sortOrder : 'none'}`
                }
              >
                <Box className={classes.headerLabel}>
                  <Typography component="span">{head.label}</Typography>
                  <IconButton
                    className={cx(
                      sortBy === head.sortField
                        ? classes.defaultSortArrow
                        : classes.nonActiveSortArrow,
                    )}
                    disableRipple
                    aria-hidden="true"
                  >
                    {sortBy === head.sortField && sortOrder === 'desc'
                      ? <ArrowDownwardIcon fontSize="small" />
                      : <ArrowUpwardIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading &&
            [...Array(8)].map((_, index) => (
              <TableRow key={index}>
                {[...Array(tableHeaders.length)].map((__, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant="rounded" height={24} />
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!loading &&
            rows.map((row) => (
              (() => {
                const rowStatusColor = getPayoutStatusColor(row.status || 'Onboarding not started');
                const waveLabel = shouldShowWaveLabel(row) ? row.label?.trim().toUpperCase() : null;
                const isActiveRow = selectedSettlementSequenceId
                  ? selectedSettlementSequenceId === row.settlementSequenceId
                  : selectedSettlementId === row.settlementId;

                return (
              <TableRow
                key={row.settlementSequenceId ?? row.settlementId}
                className={cx(classes.row, isActiveRow && classes.activeRow)}
                onClick={() => onRowClick(row.settlementId, row.settlementSequenceId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onRowClick(row.settlementId, row.settlementSequenceId);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open payout details for settlement ${row.settlementReference}`}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{row.claimant?.name || '-'}</Typography>
                    {waveLabel && <StatusChip label={waveLabel} color={palette.info.extralight} />}
                  </Box>
                </TableCell>
                <TableCell>{row.caseNumber ?? '-'}</TableCell>
                <TableCell>
                  <StatusBadge status={row.status || 'NOT_STARTED'} />
                </TableCell>
                <TableCell className={classes.amount}>{numberToUSDCurrency(row.amount || 0)}</TableCell>
                <TableCell>
                  <StatusChip label={(row.paymentMethod || 'N/A').toUpperCase()} color={rowStatusColor} />
                </TableCell>
                <TableCell>
                  <StageProgress stage={row.stage} />
                </TableCell>
                {showOwner && (
                  <TableCell>
                    <OwnerBadge owner={row.owner || 'Info'} color={rowStatusColor} />
                  </TableCell>
                )}
                <TableCell>
                  <Tooltip title={moment(row.updatedAt).format('MMM D, YYYY h:mm:ss A')}>
                    <Typography>{moment(row.updatedAt).fromNow()}</Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
                );
              })()
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WorklistTable;
